import PageHero from "@/components/PageHero";
import Reveal from "@/components/Reveal";
import { SITE } from "@/lib/api";
import { Settings2 } from "lucide-react";

const Section = ({ title, children }) => (
  <Reveal>
    <section className="rounded-3xl border border-[#D8E5E8] bg-white p-7 shadow-[0_10px_40px_-28px_rgba(16,51,59,0.3)] sm:p-10">
      <h2 className="font-display text-xl font-semibold text-[#1A2E33] sm:text-2xl">{title}</h2>
      <div className="mt-4 space-y-4 text-sm leading-relaxed text-[#5C7178] sm:text-base">{children}</div>
    </section>
  </Reveal>
);

export default function CookiePolicy() {
  return (
    <main>
      <PageHero
        eyebrow="Documento legale"
        titleLines={["Cookie Policy", "e Informativa Privacy."]}
        subtitle="Come questo sito utilizza i cookie e come trattiamo i dati che ci affidi tramite il modulo di contatto."
      />

      <div className="mx-auto max-w-4xl space-y-6 px-5 py-12 pb-28 sm:px-8" data-testid="cookie-policy-page">
        <Section title="1. Titolare del trattamento">
          <p>
            Il titolare del trattamento dei dati è {SITE.legalName}, con sede in {SITE.address},
            P.IVA / C.F. {SITE.piva}, contattabile all'indirizzo{" "}
            <a href={`mailto:${SITE.email}`} className="text-[#1F8296] underline underline-offset-2 hover:text-[#238496]">{SITE.email}</a>{" "}
            o al numero <a href={`tel:${SITE.phone}`} className="text-[#1F8296] underline underline-offset-2 hover:text-[#238496]">{SITE.phoneDisplay}</a>.
          </p>
        </Section>

        <Section title="2. Cosa sono i cookie">
          <p>
            I cookie sono piccoli file di testo che i siti visitati inviano al dispositivo dell'utente,
            dove vengono memorizzati per essere ritrasmessi agli stessi siti alla visita successiva.
            Alcuni cookie sono indispensabili al funzionamento del sito (cookie tecnici), altri — come
            quelli analitici o di profilazione — vengono installati solo con il tuo consenso.
          </p>
        </Section>

        <Section title="3. Tipologie di cookie utilizzate da questo sito">
          <p>
            <strong className="text-[#33484F]">Cookie tecnici e necessari.</strong> Servono al corretto
            funzionamento del sito: memorizzano le tue preferenze di consenso e gestiscono l'accesso
            all'area riservata. Non richiedono il consenso (art. 122 del D.Lgs. 196/2003, come novellato
            dal D.Lgs. 101/2018).
          </p>
          <p>
            <strong className="text-[#33484F]">Cookie analitici.</strong> Ci aiutano a capire come il sito
            viene utilizzato, in forma aggregata e anonima. Vengono attivati solo dopo il tuo consenso.
          </p>
          <p>
            <strong className="text-[#33484F]">Cookie di marketing e profilazione.</strong> Servono a
            proporti contenuti in linea con i tuoi interessi. Vengono attivati solo dopo il tuo consenso.
          </p>
        </Section>

        <Section title="4. Cookie e strumenti di memorizzazione utilizzati">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] border-collapse text-left text-xs sm:text-sm" data-testid="cookie-table">
              <thead>
                <tr className="border-b border-[#D8E5E8] text-[#33484F]">
                  <th className="py-3 pr-4 font-semibold">Nome</th>
                  <th className="py-3 pr-4 font-semibold">Tipologia</th>
                  <th className="py-3 pr-4 font-semibold">Finalità</th>
                  <th className="py-3 font-semibold">Durata</th>
                </tr>
              </thead>
              <tbody className="text-[#5C7178]">
                <tr className="border-b border-[#E3EDEF]">
                  <td className="py-3 pr-4 font-plex">sds_cookie_consent</td>
                  <td className="py-3 pr-4">Tecnico</td>
                  <td className="py-3 pr-4">Memorizza le preferenze di consenso ai cookie</td>
                  <td className="py-3">12 mesi</td>
                </tr>
                <tr className="border-b border-[#E3EDEF]">
                  <td className="py-3 pr-4 font-plex">access_token / refresh_token</td>
                  <td className="py-3 pr-4">Tecnico</td>
                  <td className="py-3 pr-4">Autenticazione dell'area riservata (solo amministratori)</td>
                  <td className="py-3">Sessione / 7 giorni</td>
                </tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="5. Gestione delle preferenze">
          <p>
            Al primo accesso ti viene mostrato un banner dal quale puoi accettare tutti i cookie,
            rifiutare quelli non necessari o personalizzare le scelte. Puoi modificare in qualsiasi
            momento le tue preferenze cliccando sul pulsante qui sotto.
          </p>
          <button
            type="button"
            data-testid="cookie-policy-manage-button"
            onClick={() => window.dispatchEvent(new CustomEvent("sds-open-cookie-settings"))}
            className="inline-flex items-center gap-2 rounded-full bg-[#2997AB] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#238496]"
          >
            <Settings2 className="h-4 w-4" /> Gestisci preferenze cookie
          </button>
          <p>
            Puoi inoltre disattivare i cookie direttamente dalle impostazioni del tuo browser
            (Chrome, Firefox, Safari, Edge). La disattivazione dei cookie tecnici può compromettere
            alcune funzionalità del sito.
          </p>
        </Section>

        <Section title="6. Informativa privacy — modulo di contatto (art. 13 Reg. UE 2016/679)">
          <p>
            I dati inseriti nel modulo di contatto (nome, email, telefono e contenuto del messaggio)
            vengono trattati da {SITE.legalName} al solo scopo di rispondere alla tua richiesta di
            informazioni sui corsi.
          </p>
          <p>
            La base giuridica del trattamento è l'esecuzione di misure precontrattuali richieste
            dall'interessato. I dati sono conservati per il tempo necessario a gestire la richiesta e
            non vengono ceduti a terzi né diffusi.
          </p>
          <p>
            In qualità di interessato puoi esercitare in qualsiasi momento i diritti di accesso,
            rettifica, cancellazione, limitazione e opposizione (artt. 15-22 GDPR) scrivendo a{" "}
            <a href={`mailto:${SITE.email}`} className="text-[#1F8296] underline underline-offset-2 hover:text-[#238496]">{SITE.email}</a>.
            Hai inoltre diritto di reclamo al Garante per la protezione dei dati personali.
          </p>
        </Section>

        <Section title="7. Modifiche al presente documento">
          <p>
            Il titolare può aggiornare la presente informativa in qualsiasi momento. Ti invitiamo a
            consultare periodicamente questa pagina.
          </p>
          <p className="font-plex text-xs uppercase tracking-[0.2em] text-[#7A8F96]">
            Ultimo aggiornamento: luglio 2026
          </p>
        </Section>
      </div>
    </main>
  );
}
